/* ==========================================================================
   Sound Event Detector - Comprehensive Detection Pages i18n Dictionary
   Multi-language articles across EN, CS, DE, ES, AR, PT
   ========================================================================== */

export const detectionTranslations = {
  en: {
    gunshot: {
      eyebrow: "REAL-TIME GUNSHOT DETECTION",
      title: "Gunshot Detection in Real Time",
      lead: "Instant detection of firearm discharges under 400ms with automatic PTZ camera slewing and emergency dispatch integration.",
      gain: "7x Faster Emergency Speed",
      filter: "Acoustic Impulse Classifier",
      steps: [
        { icon: "💥", title: "1. Acoustic Shockwave", desc: "Sensors detect initial muzzle flash shockwave and bullet supersonic crack." },
        { icon: "🧠", title: "2. Neural AI Match", desc: "Pattern matched against 50,000+ firearm signatures (handguns, rifles, shotguns)." },
        { icon: "📹", title: "3. PTZ Auto-Slew", desc: "Automatically turns PTZ cameras directly to the sound origin bearing." },
        { icon: "🚨", title: "4. VMS Dispatch", desc: "Sends real-time alert with audio snippet to police command center in <5s." }
      ],
      intro: "Sound Event Detector provides real-time acoustic gunshot and muzzle blast localization. Capable of pinpointing firearm discharge coordinates and slewing PTZ cameras in <5s of detonation.",
      sections: [
        {
          title: "Acoustic Impulse Shockwave Analysis",
          desc: "Our deep neural network analyzes high-amplitude acoustic shockwave impulses to identify firearm discharges instantly while rejecting false triggers like fireworks, vehicle backfires, or construction noise.",
          bullets: [
            "Covers indoor corridors, outdoor city plazas, and university campuses.",
            "Operates 24/7 in darkness, heavy fog, smoke, and complex acoustic environments.",
            "Integrates natively with Axis, Milestone, Genetec, and Hanwha VMS."
          ]
        },
        {
          title: "Target Deployment Sectors",
          desc: "The gunshot detection solution is deployed across critical public and private infrastructure:",
          bullets: [
            "Schools, Colleges & University Campuses",
            "Smart Cities, Public Plazas & Transportation Hubs",
            "Critical Infrastructure & Energy Facilities",
            "Shopping Malls, Stadiums & Military Sites"
          ]
        },
        {
          title: "100% GDPR Privacy Compliance",
          desc: "The system does not process personal voice data or record human conversations. It analyzes acoustic frequency impulses on the edge, making it suitable for deployment in sensitive public areas where cameras are restricted.",
          bullets: []
        }
      ],
      faqs: [
        { q: "How fast is gunshot detection?", a: "The system identifies gunshot acoustic signatures within milliseconds (<5s) and immediately notifies security command centers." },
        { q: "Does the system work without direct visibility?", a: "Yes, acoustic AI sensors operate outside camera coverage, in total darkness, heavy fog, smoke, and complex indoor/outdoor spaces." },
        { q: "Can the system distinguish gunshots from fireworks?", a: "Yes, advanced neural algorithms isolate true supersonic firearm shockwaves from fireworks, vehicle backfires, and industrial noise." },
        { q: "Can the system operate without cameras?", a: "Yes, SED operates independently as a standalone acoustic sensor or integrates natively into third-party VMS networks." }
      ]
    },
    glass: {
      eyebrow: "FORCED ENTRY & GLASS BREAK",
      title: "Glass Break Detection in Real Time",
      lead: "Real-time acoustic AI identification of glass fracture and window shattering for immediate burglary prevention.",
      gain: "Instant Perimeter Alerting",
      filter: "Glass Resonance Classifier",
      steps: [
        { icon: "🔨", title: "1. Impact Thud", desc: "Captures initial heavy object impact against window glass pane." },
        { icon: "📊", title: "2. Resonance Filter", desc: "Validates high-frequency glass acoustic fracture frequencies (3-8 kHz)." },
        { icon: "🔒", title: "3. Perimeter Alert", desc: "Triggers internal alarms before intruders step inside building perimeters." }
      ],
      intro: "Real-time glass break detection provides immediate identification of window shattering and forced perimeter intrusion before unauthorized individuals enter facility interiors.",
      sections: [
        {
          title: "Dual-Frequency Acoustic Analysis",
          desc: "The sensor analyzes both low-frequency thuds (initial blunt impact) and high-frequency acoustic resonance (3–8 kHz glass fracture) to eliminate false alarms caused by dropping keys or thunder.",
          bullets: [
            "Detects tempered, laminated, wired, and insulated plate glass breakage.",
            "Instant perimeter alerting before intruders cross building doors.",
            "Covers up to 15 meters radius with 360° omnidirectional sensing."
          ]
        },
        {
          title: "Key Security Benefits",
          desc: "Protects storefront windows, office building perimeters, and residential properties with immediate alert dispatch to security personnel.",
          bullets: [
            "Operates effectively in darkness and behind window blinds or curtains.",
            "Natively integrates with Axis, Milestone, and Genetec VMS platforms.",
            "Zero false alarms from ambient street traffic or thunder."
          ]
        }
      ],
      faqs: [
        { q: "How fast is glass break detection?", a: "The system detects window fracture and shattering instantly, triggering perimeter alerts before intruders step inside." },
        { q: "Does it trigger false alarms from dropping keys or thunder?", a: "No, dual-frequency analysis requires both a low-frequency impact thud and high-frequency glass resonance (3-8 kHz)." }
      ]
    },
    drone: {
      eyebrow: "AIRSPACE PROTECTION",
      title: "Acoustic Drone Detection",
      lead: "SED uses artificial intelligence to recognize the characteristic sound of drone rotors and motors, providing early warnings of their presence around critical infrastructure and protected facilities.",
      demoBtn: "Request Live Drone Demo",
      gain: "Early Airspace Warning",
      filter: "Rotary Harmonic Classifier",
      steps: [
        { icon: "🛸", title: "1. Rotary Sensing", desc: "Captures low-altitude drone motor and rotor acoustic frequencies." },
        { icon: "📡", title: "2. Acoustic Analysis", desc: "Advanced acoustic analysis of rotor blade harmonic frequencies in real time." },
        { icon: "🛡️", title: "3. Airspace Warning", desc: "Triggers early warning before drone enters restricted facility airspace." }
      ],
      intro: "Acoustic drone detection monitors low-altitude airspace for unauthorized unmanned aerial vehicles (UAVs) near critical infrastructure, prisons, and public events.",
      sections: [
        {
          title: "Harmonic Rotor Frequency Sensing",
          desc: "Identifies unique blade RPM acoustic signatures of commercial and custom drones. Differentiates aerial drones from birds, HVAC fans, and lawn mowers.",
          bullets: [
            "Early airspace warning before drones fly directly over restricted zones.",
            "Instant security alert dispatch to security personnel and control rooms.",
            "Operates 24/7 in fog, rain, darkness, and high wind conditions."
          ]
        }
      ],
      faqs: [
        { q: "How far away can SED detect drones?", a: "Acoustic detection identifies low-altitude commercial and custom drone rotor harmonics up to hundreds of meters away." },
        { q: "Does weather affect acoustic drone detection?", a: "SED operates continuously in fog, rain, darkness, and wind where optical radar or vision sensors fail." }
      ]
    },
    scream: {
      eyebrow: "MULTI-CLASS VOCAL AI & PRE-ASSAULT DE-ESCALATION",
      title: "Scream, Panic & Verbal Aggression Classification",
      lead: "SED neural networks distinctly classify 5 separate vocal acoustic categories — Female Scream, Male Scream, Children Scream, Baby Cry, and Verbal Aggression — enabling pre-assault de-escalation and precise emergency dispatch.",
      gain: "Pre-Assault De-escalation & Incident Triage",
      filter: "5-Class Vocal Stress & Formant Neural Network",
      steps: [
        { icon: "⚡", title: "1. Screams vs. Aggression", desc: "Differentiates acute terror screams (incident in progress) from hostile verbal altercations and shouting (pre-assault warning)." },
        { icon: "👶", title: "2. Age & Gender Classification", desc: "Isolates distinct acoustic formants for Female Screams, Male Screams, Children Screams, and Infant / Baby Crying." },
        { icon: "🔒", title: "3. 100% GDPR & HIPAA Compliant", desc: "Edge spectral tensor analysis with zero voice recording or wiretapping — perfectly safe for hospital ERs and schools." }
      ],
      intro: "While panic screams and verbal aggression both involve elevated human vocalization, their acoustic patterns, frequencies, and operational implications are fundamentally different. SED Sound Event Detector features separate, dedicated neural classifiers for Female Screams, Male Screams, Children Screams, Baby Crying, and Verbal Aggression. This critical distinction enables hospital staff and municipal security to de-escalate verbal hostility before violence erupts, while instantly dispatching emergency responders to acute terror screams.",
            sections: [
        {
          title: "Five Independent Voice Event Classifiers",
          desc: "Neural networks distinguish five types of vocal events and provide early alerts for situations that require immediate verification and response:",
          bullets: [
            "**Female Panic Scream:** Recognition of distinct vocal distress associated with fear or imminent danger.",
            "**Male Panic Scream:** Detection of urgent vocal expressions indicating dangerous situations or trauma.",
            "**Child Panic Scream:** Recognition of distinct child distress screams to alert to hazards or injury.",
            "**Baby Cry:** Detection of infant crying for rapid operator notification and immediate verification.",
            "**Verbal Aggression & Altercations:** Recognition of shouting, threatening vocal outbursts, and escalating conflict."
          ]
        },
        {
          title: "Why Distinguishing Screams & Aggression Matters in Practice",
          desc: "Distinguishing between different vocal event types helps security operators assess alerts accurately and choose the proper response:",
          bullets: [
            "**Faster Response:** Early notification allows operators to verify the situation and intervene when needed.",
            "**Accurate Event Assessment:** Operators receive immediate sound classification data linked to PTZ camera video feeds.",
            "**False Alarm Reduction:** AI analyzes acoustic characteristics to reliably filter out normal ambient background noise."
          ]
        }
      ],
      faqs: [
        { q: "Can SED distinguish between a panic scream and aggressive shouting?", a: "Yes. SED utilizes separate neural classifiers for Screams (acute vocal terror/assault) and Aggression (hostile verbal altercations, angry shouting). This allows security teams to intervene and de-escalate situations before physical violence breaks out." },
        { q: "How does the system distinguish between female, male, and children screams?", a: "The neural network analyzes fundamental vocal pitch (F0), formant frequency shifts, and harmonic stress contours to accurately categorize female, male, and pediatric panic screams." },
        { q: "Can SED detect baby crying in public venues?", a: "Yes. The Children Cry classifier detects the specific rhythmic acoustic signature of infant distress, helping security locate unattended or lost children in shopping malls and transit hubs." },
        { q: "Is human speech recorded or wiretapped?", a: "No. SED is 100% GDPR and HIPAA compliant. It processes acoustic spectrums locally in volatile RAM on edge processors and never records, stores, or transmits spoken words." }
      ]
    },
    graffiti: {
      eyebrow: "VANDALISM PREVENTATIVE SHIELD",
      title: "Graffiti and Spray Detection in Real Time",
      lead: "Detect spray paint aerosol hiss and ball shaker agitation before public or private property is defaced.",
      gain: "Zero Surface Vandalism",
      filter: "Aerosol Acoustic Signature",
      steps: [
        { icon: "🎨", title: "1. Agitator & Hiss", desc: "Detects aerosol nozzle pressure hiss + metal shaker ball rattle." },
        { icon: "⚡", title: "2. Instant Strobe Alert", desc: "Triggers high-decibel warning strobe or security dispatch." }
      ],
      intro: "Detect spray paint aerosol nozzle hiss and agitator shaker ball rattle in real time to stop public property vandalism before surfaces are defaced.",
      sections: [
        {
          title: "Aerosol Hiss & Ball Rattle Classifier",
          desc: "Identifies unique high-pressure spray nozzle acoustics and shaker ball agitation, preventing vandalism across historic monuments, train yards, and public buildings.",
          bullets: [
            "Stops spray vandalism before large-scale property damage occurs.",
            "Deploys in unlit alleyways, heritage sites, and railway depots.",
            "Triggers automatic audio warnings or guard dispatch."
          ]
        }
      ],
      faqs: [
        { q: "What sounds trigger graffiti detection?", a: "The system identifies high-pressure aerosol nozzle hiss combined with the acoustic rattle of spray can metal shaker balls." }
      ]
    },
    grinder: {
      eyebrow: "INDUSTRIAL PERIMETER PROTECTION",
      title: "Real-Time Grinder and Drilling Detection",
      lead: "Detect angle grinders, power drills, and forced entry metal cutting tools protecting substations and bank vaults.",
      gain: "Perimeter Breach Protection",
      filter: "Frictional Cutting Classifier",
      steps: [
        { icon: "⚙️", title: "1. Motor Friction", desc: "Senses high-RPM angle grinder motor friction acoustics." },
        { icon: "🏭", title: "2. Perimeter Guard", desc: "Alerts industrial plant security before intruders breach inner fence." }
      ],
      intro: "Detect high-speed angle grinders, power drills, and metal saws cutting perimeter fences or ATM safe vaults in real time.",
      sections: [
        {
          title: "Frictional Motor Cutting Pattern",
          desc: "Senses high-RPM electric motor friction and heavy metal cutting acoustic profiles to prevent burglary and fence breaches.",
          bullets: [
            "Monitors industrial perimeters, solar farms, and ATM vault rooms.",
            "Triggers immediate armed response team dispatch upon detection.",
            "Differentiates heavy cutting tools from ambient road traffic."
          ]
        }
      ],
      faqs: [
        { q: "What tools are detected by the grinder model?", a: "High-speed electric angle grinders, metal circular saws, power drills, and heavy demolition saws." }
      ]
    },
    siren: {
      eyebrow: "SMART CITY TRAFFIC & GARAGE ALARMS",
      title: "Real-Time Car Alarm and Siren Detection",
      lead: "Detect approaching emergency sirens and vehicle alarms for smart traffic priority and parking garage security.",
      gain: "Green Wave Priority",
      filter: "Emergency Sweep Classifier",
      steps: [
        { icon: "🚨", title: "1. Siren Tracking", desc: "Tracks periodic 400-1200 Hz emergency vehicle siren sweeps." },
        { icon: "🚗", title: "2. Alarm Guard", desc: "Monitors car break-in alarms across underground parking garages." }
      ],
      intro: "Detect vehicle alarms and approaching police, ambulance, or fire emergency sirens for smart traffic signal control and parking garage monitoring.",
      sections: [
        {
          title: "Siren Sweep & Car Alarm Tracking",
          desc: "Tracks periodic 400–1200 Hz emergency vehicle siren sweeps to communicate green waves to traffic signal controllers.",
          bullets: [
            "Prioritizes emergency vehicle traffic through busy urban intersections.",
            "Detects car break-in alarms across multi-level parking garages.",
            "Improves emergency service arrival times across smart cities."
          ]
        }
      ],
      faqs: [
        { q: "How does siren detection help municipal traffic control?", a: "Tracks emergency vehicle sirens to communicate green wave priorities to traffic signal controllers." }
      ]
    },
    other: {
      eyebrow: "EXTENDED ACOUSTIC CLASSIFIERS & CUSTOM AI",
      title: "Other / Custom Audio Event Detection",
      lead: "SED identifies a comprehensive catalog of specialized security Alerts and environmental Events beyond our primary classifiers, alongside bespoke AI acoustic models trained for client-specific applications.",
      gain: "Full Spectrum Acoustic AI",
      filter: "Multi-Model Edge Classifier & Custom R&D",
      steps: [
        { icon: "🚨", title: "1. Critical Security Alerts", desc: "Recognizes high-risk incidents: unauthorized vehicle entries in remote areas, tire squeal/drifting, fireworks, calibrated glass testing, male & child panic screams." },
        { icon: "📊", title: "2. Environmental & Operational Events", desc: "Captures ambient situations: door slams in garages and restrooms, power tools/construction, human presence in restricted zones, infant cries, transit." },
        { icon: "🎛️", title: "3. Bespoke Custom Neural Models", desc: "Trains lightweight neural models directly on client audio recordings (industrial gas leak hiss, machine bearing anomalies, custom facility alarms)." }
      ],
      intro: "SED sound detectors are capable of identifying a wide range of acoustic incidents. In general, we refer to these incidents as events, which are further categorized into Alerts and Events. The difference between alerts and events lies in their nature: alerts indicate sounds associated with dangerous or undesirable situations, while events refer to sounds that are common in most environments and provide vital situational intelligence (for example, detecting human presence or slamming doors in sterile cleanrooms, server vaults, or off-hours facilities).",
            sections: [
        {
          title: "Extended Security Detections",
          desc: "In addition to dedicated gunshot, glass break, scream, aggression, drone, and spray detection, SED can recognize additional acoustic security events depending on software edition:",
          bullets: [
            "**Vehicle:** Engine sound recognition of passing motor vehicles to warn of presence in monitored zones.",
            "**Traffic:** Road tire friction acoustic monitoring for remote perimeter awareness.",
            "**Tire Squeal:** Recognition of skidding or abrupt braking sounds indicating traffic risk or drifting.",
            "**Fireworks & Pyrotechnics:** Detection of consumer pyrotechnics allowing alerts on unauthorized discharge.",
            "**Glass Tester:** Acoustic recognition of calibration testers for streamlined commissioning."
          ]
        },
        {
          title: "Situational & Operational Detections",
          desc: "The full software edition expands system capability with additional situational sounds for cities, warehouses, prisons, transit, and industrial plants:",
          bullets: [
            "**Door Slam:** Detection of heavy door slams indicating unusual activity in secure areas.",
            "**Construction Work:** Recognition of tool sounds including drilling, cutting, or jackhammering.",
            "**People & Human Presence:** Acoustic recognition of human activity (talking, laughing, coughing, sneezing, whispering).",
            "**Children:** Recognition of children playing or shouting in monitored perimeters.",
            "**Animals & Birds:** Acoustic tracking of domestic animals and birds for perimeter monitoring.",
            "**Transport & Rail:** Characteristic sound signatures of trams, trains, buses, and transport horns.",
            "**Warning Tones & Bells:** Detection of vehicle warning chimes, municipal bells, and caution signals.",
            "**Music & Singing:** Acoustic recognition of musical instruments or amplified singing in quiet zones.",
            "**Creaking:** Detection of mechanical creaking from gates, doors, or heavy friction."
          ]
        }
      ],
      faqs: [
        { q: "What is the difference between Alerts and Events?", a: "Alerts indicate dangerous or critical situations requiring urgent security response (e.g., gunshots, screams, tire squeal, fireworks). Events refer to ambient or operational sounds that provide situational awareness (e.g., human presence, slamming doors, machinery, baby cries)." },
        { q: "Can SED detect vehicles entering national parks or restricted areas?", a: "Yes. SED includes dedicated vehicle engine and traffic rolling resistance acoustic models designed specifically for remote perimeter and national park protection." },
        { q: "Can SED be trained to recognize custom industrial or factory sounds?", a: "Yes. JALUD Embedded can capture field audio samples and train bespoke neural classification models for industrial machine failure, gas leaks, or proprietary acoustic events." },
        { q: "Can SED detect baby crying in public spaces or malls?", a: "Yes. The Children Cry event classifier identifies infant distress to help security personnel locate lost, unsupervised, or distressed children quickly." }
      ]
    }
  },
  cs: {
    gunshot: {
      eyebrow: "OKAMŽITÁ DETEKCE STŘELBY",
      title: "Detekce střelby a výstřelů v reálném čase",
      lead: "Okamžitá detekce výstřelů palných zbraní pod 400 ms s automatickým natočením PTZ kamer a integrací do velína záchranných složek.",
      gain: "7x rychlejší výjezd zásahu",
      filter: "Akustický impulsní klasifikátor",
      steps: [
        { icon: "💥", title: "1. Rázová akustická vlna", desc: "Senzory detekují počáteční rázovou vlnu výstřelu z hlavně a nadzvukový třesk kuličky." },
        { icon: "🧠", title: "2. Neuronová AI shoda", desc: "Vzor je porovnán s 50 000+ akustickými podpisy zbraní (pistole, pušky, samopaly)." },
        { icon: "📹", title: "3. PTZ auto-otisk", desc: "Automaticky natáčí PTZ kamery přímo na přesné souřadnice zdroje zvuku." },
        { icon: "🚨", title: "4. VMS dispečink", desc: "Odesílá okamžitý poplach se zvukovým výstřižkem na dispečink policie za <5s." }
      ],
      intro: "Sound Event Detector poskytuje okamžitou akustickou detekci a lokalizaci střelby. Je schopen určit přesné souřadnice palby a otočit kamery za <5s od výstřelu.",
      sections: [
        {
          title: "Analýza rázových akustických vln výstřelů",
          desc: "Náš hluboký neuronový model analyzuje akustické vlny výstřelů s vysokou amplitudou a spolehlivě odfiltruje pyrotechniku, petardy, střelbu z výfuků i staveništní hluk.",
          bullets: [
            "Pokrývá vnitřní chodby, městská náměstí i univerzitní kampusy.",
            "Funguje 24/7 ve tmě, husté mlze, kouři i náročném prostředí.",
            "Nativně se integruje do systémů Axis, Milestone, Genetec a Hanwha."
          ]
        },
        {
          title: "Cílové sektory nasazení",
          desc: "Řešení detekce střelby je nasazeno napříč klíčovou veřejnou i soukromou infrastrukturou:",
          bullets: [
            "Školy, fakulty a univerzitní kampusy",
            "Chytrá města, veřejná náměstí a dopravní uzly",
            "Kritická infrastruktura a energetické objekty",
            "Nákupní centra, stadiony a vojenské objekty"
          ]
        },
        {
          title: "100% GDPR garance ochrany soukromí",
          desc: "Systém nepracuje s osobními hlasovými údaji ani nenahrává lidské rozhovory. Zpracovává akustické impulsy lokálně na čipu, což umožňuje nasazení v citlivých oblastech.",
          bullets: []
        }
      ],
      faqs: [
        { q: "Jak rychlá je detekce střelby?", a: "Systém identifikuje akustický podpis výstřelu v řádu milisekund (<5s) a okamžitě informuje velín." },
        { q: "Funguje systém i bez přímé viditelnosti?", a: "Ano, akustické AI senzory fungují mimo zorné pole kamer, v absolutní tmě, mlze i kouři." },
        { q: "Dokáže systém rozlišit střelbu od ohňostroje?", a: "Ano, pokročilé neuronové algoritmy izolují nadzvukový třesk výstřelu od pyrotechniky a výfuků." },
        { q: "Funguje systém samostatně bez kamer?", a: "Ano, SED funguje autonomně nebo se nativně připojí k jakémukoli VMS systému." }
      ]
    },
    glass: {
      eyebrow: "NÁSILNÉ VNIKNUTÍ A TŘÍŠTĚNÍ SKLA",
      title: "Detekce tříštění skla v reálném čase",
      lead: "Okamžitá akustická identifikace lomu a tříštění skla oken a výloh pro okamžitou prevenci vloupání.",
      gain: "Okamžitá perimetrická výstraha",
      filter: "Klasifikátor rezonance skla",
      steps: [
        { icon: "🔨", title: "1. Nárazový úder", desc: "Zaznamenává počáteční úder těžkého předmětu do skleněné výplně." },
        { icon: "📊", title: "2. Rezonanční filtr", desc: "Ověřuje vysokofrekvenční akustické tříštění skla (3–8 kHz)." },
        { icon: "🔒", title: "3. Perimetrický poplach", desc: "Spouští interní alarmy dříve, než pachatel překročí práh budovy." }
      ],
      intro: "Detekce tříštění skla poskytuje okamžitou identifikaci rozbití oken a pokusu o násilné vniknutí dříve, než neoprávněné osoby vstoupí do objektu.",
      sections: [
        {
          title: "Dvoufrekvenční akustická analýza",
          desc: "Senzor analyzuje nízkofrekvenční náraz i vysokofrekvenční rezonanci tříštění skla (3–8 kHz), což eliminuje falešné poplachy při pádu klíčů či hromu.",
          bullets: [
            "Detekuje kalené, vrstvené, izolační i tabulové sklo.",
            "Okamžitý varovný signál ještě před vstupem pachatele.",
            "Pokrývá okruh až 15 metrů s 360° všesměrovým snímáním."
          ]
        }
      ],
      faqs: [
        { q: "Jak rychlá je detekce tříštění skla?", a: "Systém detekuje rozbití skla okamžitě a spouští poplach před vniknutím." },
        { q: "Vyvolá pád klíčů falešný poplach?", a: "Ne, dvoufrekvenční analýza vyžaduje současně náraz i charakteristickou rezonanci rozbitého skla." }
      ]
    },
    drone: {
      eyebrow: "OCHRANA VZDUŠNÉHO PROSTORU",
      title: "Akustická detekce dronů",
      lead: "SED pomocí umělé inteligence rozpoznává charakteristický zvuk rotorů a motorů dronů a včas upozorňuje na jejich přítomnost v okolí kritické infrastruktury a dalších chráněných objektů.",
      demoBtn: "Požádat o živou ukázku detekce dronů",
      gain: "Včasná výstraha vzdušného prostoru",
      filter: "Harmonický klasifikátor rotorů",
      steps: [
        { icon: "🛸", title: "1. Snímání rotorů", desc: "Zaznamenává akustické frekvence motorů a rotorů dronů v nízkých výškách." },
        { icon: "📡", title: "2. Akustická analýza", desc: "Pokročilá analýza harmonických frekvencí a otáček motorů dronů v reálném čase." },
        { icon: "🛡️", title: "3. Varování prostoru", desc: "Spouští varování dříve, než dron vstoupí do chráněné zóny." }
      ],
      intro: "Akustická detekce dronů monitoruje vzdušný prostor a odhaluje neoprávněné bezpilotní letouny (UAV) v blízkosti věznic, průmyslu a akcí.",
      sections: [
        {
          title: "Harmonické snímání frekvence rotorů",
          desc: "Identifikuje akustické podpisy otáček rotorů běžných i vlastních dronů. Spolehlivě rozliší dron od ptáků či sekaček na trávu.",
          bullets: [
            "Včasné varování dříve, než dron přelétne chráněnou zónu.",
            "Okamžité odeslání bezpečnostního poplachu bezpečnostní službě a dispečinku.",
            "Funguje 24/7 v mlze, dešti, tmě i silném větru."
          ]
        }
      ],
      faqs: [
        { q: "Na jakou vzdálenost SED drony detekuje?", a: "Akustická detekce rozpozná harmonické frekvence rotorů na vzdálenost stovek metrů." },
        { q: "Vadí detekci nepříznivé počasí?", a: "SED funguje nepřetržitě i v mlze, dešti a tmě, kde optické senzory selhávají." }
      ]
    },
    scream: {
      eyebrow: "VÍCETŘÍDNÍ HLASOVÁ AI & PREVENTIVNÍ DEESKALACE",
      title: "Detekce křiku, paniky a verbální agrese",
      lead: "Neuronové sítě SED spolehlivě rozlišují 5 samostatných hlasových kategorií – ženský křik, mužský křik, dětský křik, pláč dítěte a verbální agresi – což umožňuje včasnou deeskalaci i přesné zacílení zásahu.",
      gain: "Preventivní deeskalace & Třídění incidentů",
      filter: "5-třídní neuronová síť pro hlasový stres a formanty",
      steps: [
        { icon: "⚡", title: "1. Křik vs. Verbální agrese", desc: "Rozlišuje bezprostřední panické výkřiky (probíhající útok) od agresivních hádek a křiku (varování před fyzickým napadením)." },
        { icon: "👶", title: "2. Klasifikace věku a pohlaví", desc: "Přesně izoluje akustické formanty pro ženský křik, mužský křik, dětský křik a pláč kojence/dítěte." },
        { icon: "🔒", title: "3. 100% v souladu s GDPR a HIPAA", desc: "Lokální analýza frekvenčních spekter bez nahrávání hlasu či odposlechu – bezpečné pro nemocnice i školy." }
      ],
      intro: "Ačkoliv panický výkřik i verbální agrese představují hlasité lidské projevy, jejich akustický profil, frekvence a operační význam jsou zásadně odlišné. Sound Event Detector obsahuje samostatné neuronové klasifikátory pro ženský křik, mužský křik, dětský křik, pláč dítěte a verbální agresi. Toto rozlišení je klíčové: umožňuje personálu nemocnic a městské policii zasáhnout a deeskalovat slovní agresi dříve, než dojde k fyzickému násilí, a zároveň okamžitě zalarmovat hlídku při panickém křiku oběti.",
            sections: [
        {
          title: "Pět samostatných klasifikátorů hlasových událostí",
          desc: "Neuronové sítě rozlišují pět typů hlasových událostí a upozorňují na situace, které mohou vyžadovat rychlé ověření a reakci:",
          bullets: [
            "**Ženský panický křik:** Rozpoznání výrazného hlasového projevu spojeného se strachem nebo bezprostředním ohrožením.",
            "**Mužský panický křik:** Detekce naléhavého hlasového projevu, který může signalizovat nebezpečnou situaci nebo úraz.",
            "**Dětský panický křik:** Rozpoznání výrazného dětského výkřiku, který může upozornit na ohrožení nebo úraz.",
            "**Dětský pláč:** Detekce dětského pláče pro rychlé upozornění obsluhy a následné ověření situace.",
            "**Verbální agrese a hádky:** Rozpoznání křiku, výhrůžných hlasových projevů a eskalujícího konfliktu."
          ]
        },
        {
          title: "Proč je rozlišení křiku a agrese důležité v praxi",
          desc: "Rozlišení různých typů hlasových událostí pomáhá obsluze lépe vyhodnotit upozornění a zvolit další postup:",
          bullets: [
            "**Rychlejší reakce:** Včasné upozornění umožňuje obsluze situaci ověřit a podle potřeby zasáhnout.",
            "**Přesnější vyhodnocení události:** Operátor obdrží informaci o typu rozpoznaného zvuku a může ji spojit s obrazem z kamerového systému.",
            "**Omezení planých poplachů:** AI analyzuje charakter zvuku a pomáhá odlišit rizikové události od běžného okolního hluku."
          ]
        }
      ],
      faqs: [
        { q: "Dokáže SED spolehlivě rozlišit panický křik od agresivní hádky?", a: "Ano. SED obsahuje dedikované modely pro panický Křik (akutní ohrožení života/napadení) a Agresi (verbální hádky, křik a výhrůžky). To umožňuje bezpečnostní službě zasáhnout a deeskalovat situaci ještě před propuknutím fyzického násilí." },
        { q: "Jak systém rozpozná ženský, mužský a dětský křik?", a: "Neuronová síť analyzuje základní hlasovou frekvenci (F0), formantové posuny a harmonické spektrum hlasového stresu, což umožňuje spolehlivé rozdělení do jednotlivých kategorií." },
        { q: "Lze detekovat pláč malého dítěte na veřejných místech?", a: "Ano. Třída Dětský pláč (Children Cry) detekuje rytmický akustický vzor pláče batolat a pomáhá najít ztracené děti v obchodních centrech a dopravních uzlech." },
        { q: "Jsou nahrávány hlasy nebo hovory osob?", a: "Ne. SED je 100% v souladu s GDPR a HIPAA. Zvuk je analyzován v reálném čase přímo na čipu kamery bez ukládání či nahrávání obsahu hovorů." }
      ]
    },
    graffiti: {
      eyebrow: "PREVENTIVNÍ OCHRANA PŘED VANDALISMEM",
      title: "Detekce sprejování a graffiti v reálném čase",
      lead: "Detekce syčení trysky spreje a chrastění kuličky dříve, než dojde k poškození veřejného či soukromého majetku.",
      gain: "Majetek bez poškození",
      filter: "Akustický podpis aerosolu",
      steps: [
        { icon: "🎨", title: "1. Kulička a tryska", desc: "Detekuje tlakové syčení trysky spreje + chrastění kovové kuličky." },
        { icon: "⚡", title: "2. Okamžitý varovný signál", desc: "Spouští akustické varování nebo hlásí výjezd ostraze." }
      ],
      intro: "Detekuje syčení trysky aerosolového spreje a chrastění kuličky v reálném čase a zastaví vandalismus dříve, než dojde k poškození fasád.",
      sections: [
        {
          title: "Klasifikátor syčení aerosolu a chrastění kuličky",
          desc: "Identifikuje akustické vlastnosti sprejování a chrání památky, vlakové soupravy i budovy před poškozením.",
          bullets: [
            "Zastaví sprejery dříve, než vzniknou rozsáhlé škody.",
            "Nasazení v tmavých uličkách, u památek i v depech.",
            "Automatické akustické varování nebo výjezd ostrahy."
          ]
        }
      ],
      faqs: [
        { q: "Jaké zvuky spouští detekci graffiti?", a: "Systém identifikuje tlakové syčení trysky v kombinaci s akustickým chrastěním kovové kuličky ve spreji." }
      ]
    },
    grinder: {
      eyebrow: "PRŮMYSLOVÁ OCHRANA PERIMETRU",
      title: "Detekce úhlové brusky a vrtačky v reálném čase",
      lead: "Detekce úhlových brusek, vrtaček a páčidel chránící trafostanice, solární parky a trezory bank.",
      gain: "Ochrana před překonáním plotu",
      filter: "Třecí klasifikátor řezání",
      steps: [
        { icon: "⚙️", title: "1. Tření motoru", desc: "Vnímá vysoké otáčky elektromotoru a tření brusky o kov." },
        { icon: "🏭", title: "2. Ochrana perimetru", desc: "Varuje ostrahu podniku dříve, než pachatel překoná plot." }
      ],
      intro: "Detekuje vysokootáčkové úhlové brusky, vrtačky a pily při řezání plotů či trezorů v reálném čase.",
      sections: [
        {
          title: "Akustické vlastnosti třecího řezání kovu",
          desc: "Identifikuje charakteristický zvuk řezání kovu a zabraňuje krádežím i vloupání do průmyslových objektů.",
          bullets: [
            "Monitoruje průmyslové areály, fotovoltaiky a bankovní trezory.",
            "Okamžitě vysílá zásahovou jednotku.",
            "Spolehlivě odliší brusku od okolního silničního provozu."
          ]
        }
      ],
      faqs: [
        { q: "Jaké nástroje model detekuje?", a: "Vysokootáčkové úhlové brusky, kotoučové pily na kov, vrtačky a rozbrusy." }
      ]
    },
    siren: {
      eyebrow: "SMART CITY DOPRAVA A GARÁŽE",
      title: "Detekce autoalarmů a sirén v reálném čase",
      lead: "Detekce přijíždějících sirén záchranářů pro prioritu na semaforech a sledování autoalarmů v garážích.",
      gain: "Zelená vlna pro záchranáře",
      filter: "Klasifikátor sirénových tónů",
      steps: [
        { icon: "🚨", title: "1. Sledování sirény", desc: "Sleduje periodický frekvenční přechod sirén záchranných složek." },
        { icon: "🚗", title: "2. Ochrana garáží", desc: "Monitoruje poplašné sirény vozidel v podzemních garážích." }
      ],
      intro: "Detekuje autoalarmy a sirény policie, sanitních vozů či hasičů pro přednost v jízdě na křižovatkách a sledování garáží.",
      sections: [
        {
          title: "Sledování sirén a autoalarmů",
          desc: "Sleduje frekvenční průběh sirén (400–1200 Hz) a předává požadavky na zelenou vlnu do řadičů semaforů.",
          bullets: [
            "Prioritizuje průjezd záchranářů rušnými křižovatkami.",
            "Detekuje vloupání do vozidel v garážových domech.",
            "Zkracuje dojezdové časy záchranných složek ve městech."
          ]
        }
      ],
      faqs: [
        { q: "Jak detekce sirén pomáhá dopravě ve městech?", a: "Sleduje tóny sirén a předává požadavek na zelenou vlnu do řídicích systémů křižovatek." }
      ]
    },
    other: {
      eyebrow: "ROZŠÍŘENÉ AKUSTICKÉ KLASIFIKÁTORY A VLASTNÍ AI",
      title: "Zakázkové a průmyslové zvuky",
      lead: "SED dokáže identifikovat širokou škálu akustických incidentů rozdělených na bezpečnostní Výstrahy (Alerts), provozní Události (Events) a zakázkové neuronové modely na míru.",
      gain: "Plnospektrální akustická AI",
      filter: "Více-třídní Edge klasifikátor & Zakázkový výzkum",
      steps: [
        { icon: "🚨", title: "1. Bezpečnostní Výstrahy (Alerts)", desc: "Okamžité rozpoznání rizikových situací: vjezd vozidel do chráněných zón, pískání pneumatik/driftování, pyrotechnika, kalibrační tester skla, mužský a dětský křik." },
        { icon: "📊", title: "2. Situační Události (Events)", desc: "Přehled o běžném i nežádoucím dění: bouchání dveří v garážích a toaletách, stavební nářadí, přítomnost lidí ve sterilních zónách, dětský pláč, doprava." },
        { icon: "🎛️", title: "3. Zakázkové AI neuronové modely", desc: "Trénování neuronových sítí na specifické zvuky zákazníka (úniky plynu, ložiskové vady strojů, zakázkové alarmy)." }
      ],
      intro: "Zvukové detektory SED jsou schopny identifikovat širokou škálu akustických incidentů. Obecně tyto incidenty označujeme jako události, které dále dělíme na Výstrahy (Alerts) a Události (Events). Rozdíl mezi výstrahami a událostmi spočívá v jejich povaze: výstrahy indikují zvuky spojené s nebezpečnými nebo nežádoucími situacemi, zatímco události představují zvuky běžného prostředí, které však v určitých scénářích poskytují zásadní informace (např. detekce přítomnosti lidí nebo bouchání dveří v místech a časech, kde se nikdo nemá nacházet).",
            sections: [
        {
          title: "Rozšířené bezpečnostní detekce",
          desc: "Vedle specializované detekce výstřelů, tříštění skla, křiku, agrese, dronů a sprejování může SED podle zvolené verze softwaru rozpoznávat také další akustické události:",
          bullets: [
            "**Vozidlo:** Rozpoznání zvuku motoru projíždějícího vozidla pro upozornění na jeho přítomnost v monitorovaném prostoru.",
            "**Silniční provoz:** Rozpoznání hluku pneumatik projíždějícího vozidla pro včasné upozornění na pohyb v monitorované oblasti.",
            "**Pískání pneumatik:** Rozpoznání zvuku smyku nebo prudkého brzdění, který může upozornit na rizikovou dopravní situaci.",
            "**Ohňostroje a petardy:** Detekce zvuku zábavní pyrotechniky umožňující upozornit na její použití v monitorované oblasti.",
            "**Tester skla:** Rozpoznání zvuku kalibračního testeru pro snadnější ověření správného nastavení detekce tříštění skla při instalaci."
          ]
        },
        {
          title: "Situační a provozní detekce",
          desc: "Plná verze softwaru rozšiřuje možnosti systému o další zvuky využitelné ve městech, skladech, věznicích, dopravě a průmyslových provozech:",
          bullets: [
            "**Bouchání dveří:** Detekce výrazného prásknutí dveřmi, které může upozornit na neobvyklou událost v monitorovaném prostoru.",
            "**Stavební práce:** Rozpoznání vybraných zvuků práce s nářadím, například vrtání, řezání nebo použití sbíječky.",
            "**Lidé a přítomnost:** Rozpoznání zvuků lidské přítomnosti, jako jsou řeč, smích, kašlání, kýchání, šeptání nebo pískání.",
            "**Děti:** Rozpoznání zvuků hrajících si nebo křičících dětí v monitorovaném prostoru.",
            "**Zvířata a ptáci:** Rozpoznání vybraných zvuků zvířat a ptactva pro sledování jejich přítomnosti nebo pohybu.",
            "**Doprava a vlaky:** Rozpoznání charakteristických zvuků tramvají, vlaků, autobusů a dopravních klaksonů.",
            "**Výstražné zvuky a zvonky:** Detekce výstražných zvuků vozidel, městských zvonů a dalších upozorňujících tónů.",
            "**Hudba a zpěv:** Rozpoznání zvuků hudebních nástrojů nebo zpěvu například v klidových zónách.",
            "**Vrzání:** Detekce vrzání dveří, vrat nebo jiného mechanického tření."
          ]
        }
      ],
      faqs: [
        { q: "Jaký je rozdíl mezi Výstrahami (Alerts) a Událostmi (Events)?", a: "Výstrahy (Alerts) označují rizikové a nebezpečné situace vyžadující okamžitý zásah (střelba, křik, pískání pneumatik, pyrotechnika). Události (Events) představují zvuky prostředí, které pomáhají monitorovat provoz a odhalovat neoprávněný pohyb osob." },
        { q: "Dokáže SED detekovat vjezd vozidel do národního parku?", a: "Ano. Detektor obsahuje specializovanou třídu pro zvuk motoru a valivý odpor pneumatik navrženou pro odlehlé chráněné přírodní oblasti a perimetry." },
        { q: "Lze natrénovat SED na zcela specifický průmyslový zvuk?", a: "Ano. JALUD Embedded zajišťuje nahrání vzorků a natrénování zakázkového neuronového modelu např. pro úniky plynu, havárie strojů nebo specifické hrozby." },
        { q: "K čemu slouží detekce dětského pláče?", a: "Třída Dětský pláč (Children Cry) pomáhá bezpečnostní službě v obchodních centrech a na nádražích včas identifikovat ztracené či ohrožené děti bez dozoru." }
      ]
    }
  },
  de: {
    gunshot: {
      eyebrow: "ECHTZEIT-SCHUSSERKENNUNG",
      title: "Akustische Schusserkennung in Echtzeit",
      lead: "Sofortige Erkennung von Schüssen unter 400ms mit automatischer PTZ-Kameraausrichtung und Notruf-Integration.",
      gain: "7x schnellerer Einsatzausrufung",
      filter: "Impuls-Klassifikator",
      steps: [
        { icon: "💥", title: "1. Akustische Schockwelle", desc: "Erkennt Knallwelle und Überschallknall." },
        { icon: "🧠", title: "2. Neuronale KI-Abgleich", desc: "Vergleicht mit 50.000+ Waffenakustiken." },
        { icon: "📹", title: "3. PTZ-Auto-Ausrichtung", desc: "Richtet PTZ-Kameras exakt auf Geräuschquelle aus." },
        { icon: "🚨", title: "4. VMS-Alarmierung", desc: "Sendet Echtzeit-Alarm an Leitstelle in <5s." }
      ],
      intro: "Erkennt Schüsse in Echtzeit und richtet PTZ-Kameras in <5s präzise aus.",
      sections: [
        {
          title: "Schockwellen-Analyse",
          desc: "Filtert Fehlalarme wie Feuerwerk oder Baustellenlärm zuverlässig heraus.",
          bullets: ["Schützt Innenräume & Außenbereiche", "100% DSGVO-konform"]
        }
      ],
      faqs: [{ q: "Wie schnell ist die Erkennung?", a: "Erkennung in in <5s." }]
    },
    glass: {
      eyebrow: "EINBRUCHSCHUTZ & GLASBRUCH",
      title: "Glasbrucherkennung in Echtzeit",
      lead: "Erkennt Glasbruch sofort beim Aufprall vor dem Eindringen.",
      gain: "Sofortiger Perimeterschutz",
      filter: "Glas-Resonanz-Filter",
      steps: [{ icon: "🔨", title: "1. Aufprall", desc: "Erkennt Aufschlag auf Glasscheibe." }],
      intro: "Sofortige Erkennung von Glasbruch zur Einbruchsprävention.",
      sections: [{ title: "Zwei-Frequenz-Analyse", desc: "Kombiniert Aufprall und Glasfrequenz.", bullets: [] }],
      faqs: [{ q: "Reagiert das System auf Schlüsselklirren?", a: "Nein, Zwei-Frequenz-Filterung verhindert Fehlalarme." }]
    },
    drone: {
      eyebrow: "LUFTRAUMSCHUTZ",
      title: "Akustische Drohnenerkennung",
      lead: "SED nutzt künstliche Intelligenz, um das charakteristische Geräusch von Drohnenrotoren und -motoren zu erkennen und frühzeitig vor unbefugten Drohnen in der Nähe kritischer Infrastruktur zu warnen.",
      demoBtn: "Live-Demo anfordern",
      gain: "Frühwarnung im Luftraum",
      filter: "Rotoren-Harmonie-Filter",
      steps: [{ icon: "🛸", title: "1. Rotorenerfassung", desc: "Erfasst Drohnengeräusche." }],
      intro: "Akustischer Schutz vor unbefugten Drohnen.",
      sections: [{ title: "Frequenzüberwachung", desc: "Erkennt Drohnenmotoren zuverlässig.", bullets: [] }],
      faqs: [{ q: "Welche Reichweite hat das System?", a: "Erkennt Drohnen in Hunderten Metern Entfernung." }]
    },
    scream: {
      eyebrow: "MEHRKLASSEN-STIMM-KI & DEESKALATION",
      title: "Erkennung von Schreien, Panik & verbaler Aggression",
      lead: "SED-Neuronale Netze unterscheiden 5 akustische Stimmkategorien – Frauenschrei, Männerschrei, Kinderschrei, Babyweinen und verbale Aggression – zur frühzeitigen Deeskalation und Einsatzoptimierung.",
      gain: "Präventive Deeskalation & Lagebeurteilung",
      filter: "5-Klassen-Stimmstress- und Formant-Filter",
      steps: [
        { icon: "⚡", title: "1. Schreie vs. Aggression", desc: "Unterscheidet akute Panikschreie (akuter Notfall) von aggressiven Auseinandersetzungen (Frühwarnung vor Gewalt)." },
        { icon: "👶", title: "2. Alters- & Geschlechtertrennung", desc: "Isoliert exakte Formanten für Frauenschreie, Männerschreie, Kinderschreie und weinende Babys." },
        { icon: "🔒", title: "3. 100% DSGVO & HIPAA konform", desc: "Reine Frequenzspektrumanalyse auf Edge-Prozessoren ohne Sprachaufzeichnung oder Abhören." }
      ],
      intro: "Obwohl Panikschreie und verbale Aggression beides laute Vokalisationen darstellen, sind ihre akustischen Merkmale und Interventionsmöglichkeiten grundlegend verschieden. Der Sound Event Detector verfügt über separate neuronale Klassifikatoren für Frauenschreie, Männerschreie, Kinderschreie, Babyweinen und verbale Aggression. Diese Differenzierung ermöglicht es Krankenhauspersonal und Leitstellen, verbale Feindseligkeiten vor Gewaltausbrüchen zu deeskalieren.",
            sections: [
        {
          title: "Fünf unabhängige Sprachereignis-Klassifikatoren",
          desc: "Neuronale Netze unterscheiden fünf Arten von Sprachereignissen für eine schnelle Überprüfung und Reaktion:",
          bullets: [
            "**Weiblicher Panikschrei:** Erkennung von Vokalausbrüchen bei akuter Bedrohung oder Gefahr.",
            "**Männlicher Panikschrei:** Erkennung dringlicher Notrufe bei Gefahr oder Verletzungen.",
            "**Kindlicher Panikschrei:** Erkennung von Kinderschreien zur schnellen Warnung bei Zwischenfällen.",
            "**Babyweinen:** Erkennung von Säuglingsweinen zur schnellen Benachrichtigung des Personals.",
            "**Verbale Aggression & Streit:** Erkennung von Geschrei, Drohungen und eskalierenden Konflikten."
          ]
        },
        {
          title: "Warum die Unterscheidung von Schrei und Aggression in der Praxis wichtig ist",
          desc: "Die Unterscheidung verschiedener Vokalereignisse hilft dem Sicherheitspersonal bei der schnellen Lagebewertung:",
          bullets: [
            "**Schnellere Reaktion:** Frühzeitige Warnung ermöglicht gezieltes Eingreifen vor einer Eskalation.",
            "**Präzise Ereignisbewertung:** Das Sicherheitspersonal erhält genaue Ereignistypdaten verknüpft mit Kameras.",
            "**Reduzierung von Fehlalarmen:** Die KI filtert normale Umgebungsgeräusche zuverlässig heraus."
          ]
        }
      ],
      faqs: [
        { q: "Kann SED zwischen einem Schrei und aggressivem Streit unterscheiden?", a: "Ja. SED verwendet separate neuronale Modelle für Schreie (akute Lebensgefahr/Angriff) und Aggression (verbale Feindseligkeit, lautes Streiten), um Konflikte vor körperlicher Gewalt zu deeskalieren." },
        { q: "Wie unterscheidet das System Frauen-, Männer- und Kinderschreie?", a: "Das neuronale Netz analysiert Grundfrequenzen (F0), Formantverschiebungen und hormonelle Stressmuster der menschlichen Stimme." }
      ]
    },
    graffiti: {
      eyebrow: "VANDALISMUSSCHUTZ",
      title: "Graffiti- & Sprüherkennung in Echtzeit",
      lead: "Erkennt Sprühdosengeräusche vor der Beschädigung von Eigentum.",
      gain: "Schutz von Oberflächen",
      filter: "Aerosol-Akustik-Filter",
      steps: [{ icon: "🎨", title: "1. Zischen & Kugel", desc: "Erkennt Sprühdose & Mischkugel." }],
      intro: "Verhindert Vandalismus durch frühzeitige Alarmierung.",
      sections: [{ title: "Sprüh-Klassifikator", desc: "Erkennt Zischgeräusche von Dosen.", bullets: [] }],
      faqs: [{ q: "Welche Geräusche lösen Alarm aus?", a: "Düsenzischen und Mischkugelklinken." }]
    },
    grinder: {
      eyebrow: "INDUSTRIE-PERIMETERSCHUTZ",
      title: "Trennschleifer- & Bohrererkennung",
      lead: "Erkennt Trennschleifer und Metallbohren an Umspannwerken und Tresoren.",
      gain: "Perimeterschutz",
      filter: "Schneid-Klassifikator",
      steps: [{ icon: "⚙️", title: "1. Motorreibung", desc: "Erkennt Winkelschleifer-Frequenzen." }],
      intro: "Schützt kritische Infrastruktur vor gewaltsamem Eindringen.",
      sections: [{ title: "Metallschneid-Analyse", desc: "Filtert Trennschleifer aus Verkehrslärm heraus.", bullets: [] }],
      faqs: [{ q: "Welche Werkzeuge werden erkannt?", a: "Winkelschleifer, Bohrmaschinen und Kettensägen." }]
    },
    siren: {
      eyebrow: "SMART CITY & ALARM",
      title: "Sirenen- & Autoalarmerkennung",
      lead: "Erkennt Einsatzsirenen für Ampelpriorisierung.",
      gain: "Grüne Welle für Einsatzkräfte",
      filter: "Sirenen-Sweep-Filter",
      steps: [{ icon: "🚨", title: "1. Sirenenverfolgung", desc: "Erfasst Einsatzfahrzeuge." }],
      intro: "Erkennt Sirenen zur bevorzugten Ampelschaltung.",
      sections: [{ title: "Sirenen-Erkennung", desc: "Steuert grüne Wellen für Rettungskräfte.", bullets: [] }],
      faqs: [{ q: "Wie hilft Sirenenerkennung?", a: "Priorisiert Einsatzfahrzeuge an Ampeln." }]
    },
    other: {
      eyebrow: "ERWEITERTE AKUSTISCHE KLASSIFIKATOREN & KI",
      title: "Sonstiges / Kundenspezifisches Audio",
      lead: "SED erkennt einen umfassenden Katalog spezialisierter Sicherheitswarnungen (Alerts), Umweltereignisse (Events) und maßgeschneiderter KI-Modelle für Kundenanwendungen.",
      gain: "Vollspektrum Akustik-KI",
      filter: "Multi-Klassen-Klassifikator & Kunden-KI",
      steps: [
        { icon: "🚨", title: "1. Sicherheitswarnungen (Alerts)", desc: "Erkennt Fahrzeuge in Schutzgebieten, Reifenquietschen, Feuerwerk, Glas-Tester, Panikschreie von Männern und Kindern." },
        { icon: "📊", title: "2. Umweltereignisse (Events)", desc: "Erfasst zuschlagende Türen in Garagen, Werkzeuge/Bauarbeiten, menschliche Anwesenheit in Reinräumen, weinende Babys, Verkehr." },
        { icon: "🎛️", title: "3. Kundenspezifische KI-Modelle", desc: "Trainiert neuronale Modelle auf Kunden-Audiodaten (Gasaustritt, Lagerdefekte, kundenspezifische Alarme)." }
      ],
      intro: "SED-Schalldetektoren sind in der Lage, ein breites Spektrum akustischer Vorfälle zu identifizieren, unterteilt in Warnungen (Alerts) und Ereignisse (Events). Warnungen kennzeichnen gefährliche oder unerwünschte Situationen, während Ereignisse alltägliche Geräusche beschreiben, die in bestimmten Szenarien wertvolle Lageinformationen liefern (z.B. Personenanwesenheit in Sperrbereichen oder zuschlagende Türen).",
            sections: [
        {
          title: "Erweiterte Sicherheitserkennungen",
          desc: "Neben der spezialisierten Erkennung von Schüssen, Glasbruch, Schreien, Drohnen und Sprayen erkennt SED weitere Sicherheitsereignisse:",
          bullets: [
            "**Fahrzeug:** Erkennung von Motorengeräuschen zur Warnung vor Fahrzeugbewegungen.",
            "**Straßenverkehr:** Akustische Überwachung von Reifenrollgeräuschen an Außenperimetern.",
            "**Reifenquietschen:** Erkennung von Schleuder- und Vollbremsgeräuschen im Straßenverkehr.",
            "**Feuerwerk & Pyrotechnik:** Erkennung von Feuerwerkskörpern in geschützten Zonen.",
            "**Glastester:** Erkennung von Kalibriertestern für einfache Inbetriebnahme."
          ]
        },
        {
          title: "Situations- und Betriebsüberwachung",
          desc: "Die Vollversion erweitert das System um Situationsgeräusche für Städte, Hallen, Justizvollzug und Industrie:",
          bullets: [
            "**Türzuschlagen:** Erkennung lauten Zuschlagens von Türen in Sicherheitsbereichen.",
            "**Bauarbeiten:** Erkennung von Werkzeuggeräuschen wie Bohren, Sägen oder Hämmern.",
            "**Menschliche Anwesenheit:** Erkennung von Sprache, Lachen, Husten, Niesen oder Flüstern in Sperrbereichen.",
            "**Kinder:** Erkennung spielender oder rufender Kinder auf Arealen.",
            "**Tiere & Vögel:** Akustische Erfassung von Tieren und Vogelschwärmen.",
            "**Verkehr & Bahn:** Charakteristische Geräusche von Bahnen, Zügen und Bussen.",
            "**Warntöne & Glocken:** Erkennung von Fahrzeughupen und Alarmtönen.",
            "**Musik & Gesang:** Erkennung von Instrumenten oder Gesang in Ruhezonen.",
            "**Quietschen:** Erkennung von mechanischem Quietschen an Toren und Türen."
          ]
        }
      ],
      faqs: [
        { q: "Was unterscheidet Warnungen (Alerts) von Ereignissen (Events)?", a: "Warnungen betreffen akute Gefahren (Schüsse, Schreie, Reifenquietschen). Ereignisse liefern Lageinformationen über Umgebungsgeräusche (menschliche Anwesenheit, zuschlagende Türen)." },
        { q: "Können Fahrzeuge in Nationalparks erkannt werden?", a: "Ja, SED verfügt über Modelle für Motorgeräusche und Reifenabrollen speziell für abgelegene Schutzgebiete." },
        { q: "Können Spezialgeräusche gelernt werden?", a: "Ja, wir trainieren KI-Modelle auf kundenspezifische Industrie- oder Alarmgeräusche." }
      ]
    }
  },
  es: {
    gunshot: {
      eyebrow: "DETECCIÓN DE DISPAROS EN TIEMPO REAL",
      title: "Detección de Disparos en Tiempo Real",
      lead: "Detección instantánea de disparos en menos de 400 ms con orientación automática de cámaras PTZ.",
      gain: "Despacho 7x más rápido",
      filter: "Clasificador de Impulsos",
      steps: [{ icon: "💥", title: "1. Onda de choque", desc: "Detecta la onda expansiva del disparo." }],
      intro: "Localización acústica de disparos en tiempo real orientando cmaras PTZ en <5s.",
      sections: [{ title: "Análisis de Onda Expansiva", desc: "Filtra fuegos artificiales y ruidos de tráfico.", bullets: [] }],
      faqs: [{ q: "¿Qué tan rápida es la detección?", a: "Respuesta en <5s." }]
    },
    glass: {
      eyebrow: "ROTURA DE CRISTAL E INTRUSIÓN",
      title: "Detección de Rotura de Cristal",
      lead: "Identificación acústica de rotura de ventanas para prevenir robos.",
      gain: "Alerta Perimetral Instantánea",
      filter: "Filtro de Resonancia",
      steps: [{ icon: "🔨", title: "1. Impacto", desc: "Capta el impacto inicial." }],
      intro: "Detección de cristales rotos antes de que accedan al edificio.",
      sections: [{ title: "Análisis de Doble Frecuencia", desc: "Combina impacto y rotura.", bullets: [] }],
      faqs: [{ q: "¿Genera falsas alarmas?", a: "No, la doble frecuencia evita falsas alarmas." }]
    },
    drone: {
      eyebrow: "PROTECCIÓN DEL ESPACIO AÉREO",
      title: "Detección Acústica de Drones",
      lead: "SED utiliza inteligencia artificial para reconocer el sonido característico de los rotores y motores de drones, advirtiendo oportunamente sobre su presencia en infraestructuras críticas.",
      demoBtn: "Solicitar Demostración en Vivo",
      gain: "Alerta Temprana de Espacio Aéreo",
      filter: "Filtro Armónico de Rotores",
      steps: [{ icon: "🛸", title: "1. Captura de rotores", desc: "Capta frecuencias de drones." }],
      intro: "Detección de drones no autorizados en zonas sensibles.",
      sections: [{ title: "Frecuencia Armónica", desc: "Identifica revoluciones de hélices.", bullets: [] }],
      faqs: [{ q: "¿A qué distancia detecta drones?", a: "A cientos de metros de distancia." }]
    },
    scream: {
      eyebrow: "IA VOCAL MULTI-CLASE Y DESESCALADA PREVENTIVA",
      title: "Detección de Gritos, Pánico y Agresividad Verbal",
      lead: "Las redes neuronales de SED clasifican 5 categorías acústicas vocales: grito femenino, grito masculino, grito infantil, llanto de bebé y agresividad verbal, facilitando la desescalada preventiva.",
      gain: "Desescalada Preventiva y Triaje de Incidentes",
      filter: "Red Neuronal de Formantes y Estrés Vocal de 5 Clases",
      steps: [
        { icon: "⚡", title: "1. Gritos vs. Agresión Verbal", desc: "Diferencia gritos agudos de terror (incidente en curso) de disputas y discusiones verbales hostiles (fase previa al asalto)." },
        { icon: "👶", title: "2. Clasificación por Edad y Género", desc: "Aísla formantes acústicos para gritos de mujeres, hombres, niños y llanto de bebés." },
        { icon: "🔒", title: "3. 100% Compatible con RGPD e HIPAA", desc: "Análisis espectral en procesadores Edge sin grabación ni almacenamiento de voz humana." }
      ],
      intro: "Aunque los gritos de pánico y la agresión verbal implican vocalizaciones humanas elevadas, sus patrones acústicos y respuestas operativas son totalmente distintos. Sound Event Detector integra clasificadores neuronales independientes para Grito Femenino, Grito Masculino, Grito Infantil, Llanto de Bebé y Agresividad Verbal. Esta distinción permite desescalar discusiones en urgencias hospitalarias antes de que ocurra una agresión física.",
            sections: [
        {
          title: "Cinco clasificadores independientes de eventos de voz",
          desc: "Las redes neuronales distinguen cinco tipos de eventos vocales para una verificación y respuesta rápidas:",
          bullets: [
            "**Grito de pánico femenino:** Reconocimiento de expresiones vocales de angustia asociadas con peligro inminente.",
            "**Grito de pánico masculino:** Detección de expresiones vocales urgentes que indican situaciones de peligro o lesiones.",
            "**Grito de pánico infantil:** Reconocimiento de gritos agudos de niños para alertar sobre peligros o caídas.",
            "**Llanto de bebé:** Detección del llanto de bebés para una notificación rápida y verificación inmediata.",
            "**Agresión verbal y disputas:** Reconocimiento de gritos, amenazas y conflictos en escalada."
          ]
        },
        {
          title: "Por qué la distinción entre grito y agresión es vital en la práctica",
          desc: "Diferenciar entre tipos de eventos vocales ayuda a los operadores a evaluar las alertas con precisión:",
          bullets: [
            "**Respuesta más rápida:** La alerta temprana permite verificar la situación e intervenir a tiempo.",
            "**Evaluación precisa:** Los operadores reciben datos clasificados vinculados con cámaras PTZ.",
            "**Reducción de falsas alarmas:** La IA analiza las características acústicas filtrando ruidos ambientales."
          ]
        }
      ],
      faqs: [
        { q: "¿Puede SED distinguir entre un grito de pánico y una discusión agresiva?", a: "Sí. SED utiliza clasificadores independientes para Gritos (pánico inminente/asalto) y Agresión (disputas verbales y hostilidad), permitiendo intervenir antes de que estalle la violencia física." },
        { q: "¿Cómo distingue entre gritos de mujeres, hombres y niños?", a: "La red neuronal analiza el tono fundamental (F0), los formantes acústicos y el estrés armónico de las cuerdas vocales." }
      ]
    },
    graffiti: {
      eyebrow: "PROTECCIÓN CONTRA VANDALISMO",
      title: "Detección de Grafiti y Spray",
      lead: "Detecta el silbido de botes de spray antes de dañar paredes.",
      gain: "Superficies sin Vandalismo",
      filter: "Filtro Acústico de Aerosol",
      steps: [{ icon: "🎨", title: "1. Silbido y bola", desc: "Detecta aerosol y canica metálica." }],
      intro: "Evita pintadas mediante alerta temprana.",
      sections: [{ title: "Silbido de Aerosol", desc: "Identifica la boquilla de spray.", bullets: [] }],
      faqs: [{ q: "¿Qué sonidos activan la alerta?", a: "Silbido de presión y canica mezcladora." }]
    },
    grinder: {
      eyebrow: "PROTECCIÓN INDUSTRIAL PERIMETRAL",
      title: "Detección de Radial y Taladros",
      lead: "Detecta radiales y amoladoras en vallas y cajas fuertes.",
      gain: "Protección Perimetral",
      filter: "Clasificador de Fricción",
      steps: [{ icon: "⚙️", title: "1. Fricción", desc: "Detecta corte de metal." }],
      intro: "Protección contra corte de vallas y herramientas pesadas.",
      sections: [{ title: "Patrón de Corte", desc: "Separa radiales del tráfico habitual.", bullets: [] }],
      faqs: [{ q: "¿Qué herramientas detecta?", a: "Amoladoras, taladros y sierras de metal." }]
    },
    siren: {
      eyebrow: "TRÁFICO E INTEGRA ALARMAS",
      title: "Detección de Sirenas y Alarmas",
      lead: "Detecta sirenas de emergencia para semáforos inteligentes.",
      gain: "Prioridad en Semáforos",
      filter: "Filtro de Sirenas",
      steps: [{ icon: "🚨", title: "1. Rastreo de sirena", desc: "Capta vehículos de emergencia." }],
      intro: "Prioridad de semáforos para ambulancias y policía.",
      sections: [{ title: "Rastreo de Sirenas", desc: "Acelera el paso de emergencias.", bullets: [] }],
      faqs: [{ q: "¿Cómo ayuda al tráfico?", a: "Conecta la onda verde para ambulancias." }]
    },
    other: {
      eyebrow: "CLASIFICADORES ACÚSTICOS EXTENDIDOS E IA A MEDIDA",
      title: "Otros / Audio personalizado",
      lead: "SED identifica un catálogo exhaustivo de Alertas de seguridad y Eventos ambientales, junto con modelos neuronales de IA entrenados a medida para clientes.",
      gain: "IA Acústica de Espectro Completo",
      filter: "Clasificador Edge Multi-Clase e I+D",
      steps: [
        { icon: "🚨", title: "1. Alertas Críticas de Seguridad", desc: "Reconoce vehículos en zonas remotas, chirrido de neumáticos, pirotecnia, tester de vidrio, gritos de pánico de hombres y niños." },
        { icon: "📊", title: "2. Eventos Ambientales", desc: "Detecta portazos en garajes, herramientas/obras, presencia humana en zonas estériles, llanto de bebés y transporte." },
        { icon: "🎛️", title: "3. Modelos IA Personalizados", desc: "Entrena modelos ligeros para chips y procesadores edge (Axis, Hanwha, Hikvision...) con grabaciones del cliente (fugas de gas, fallos de rodamientos)." }
      ],
      intro: "Los detectores SED identifican una amplia gama de incidentes acústicos divididos en Alertas (Alerts) y Eventos (Events). Las alertas indican situaciones peligrosas o indeseables, mientras que los eventos corresponden a sonidos ambientales que aportan información situacional crítica (por ejemplo, detectar presencia humana o portazos en zonas restringidas).",
            sections: [
        {
          title: "Detecciones de Seguridad Ampliadas",
          desc: "Además de las detecciones dedicadas de disparos, cristales rotos, gritos, drones y grafitis, SED reconoce eventos acústicos adicionales:",
          bullets: [
            "**Vehículo:** Reconocimiento del motor de vehículos para advertir de presencia en áreas restringidas.",
            "**Tráfico vial:** Monitoreo acústico del rodamiento de neumáticos en perímetros remotos.",
            "**Chirrido de neumáticos:** Reconocimiento de derrapes o frenadas bruscas en vías públicas.",
            "**Fuegos artificiales:** Detección de pirotecnia para control en zonas protegidas.",
            "**Probador de vidrio:** Reconocimiento de emisores de calibración para puesta en marcha."
          ]
        },
        {
          title: "Detección Situacional y Operativa",
          desc: "La versión completa amplía las capacidades con sonidos situacionales para ciudades, almacenes y fábricas:",
          bullets: [
            "**Portazo:** Detección de portazos fuertes en zonas seguras o accesos restringidos.",
            "**Obras y herramientas:** Reconocimiento de sonidos de taladros, cortes o martillos.",
            "**Presencia humana:** Reconocimiento acústico de voces, risas, tos o susurros en áreas estériles.",
            "**Niños:** Reconocimiento de niños jugando o gritando en perímetros monitoreados.",
            "**Animales y aves:** Monitoreo acústico de fauna en perímetros aeroportuarios.",
            "**Transporte y trenes:** Firmas acústicas de tranvías, trenes, autobuses y bocinas.",
            "**Tonos de advertencia:** Detección de sirenas de vehículos y campanas de alarma.",
            "**Música y canto:** Reconocimiento de instrumentos musicales en zonas de silencio.",
            "**Crujidos mecánicos:** Detección de fricción mecánica y chirridos en cancelas y puertas."
          ]
        }
      ],
      faqs: [
        { q: "¿Cuál es la diferencia entre Alertas y Eventos?", a: "Las Alertas indican peligros urgentes (disparos, gritos, derrapes). Los Eventos aportan contexto ambiental operativo (presencia humana, portazos, llanto de niños)." },
        { q: "¿Detecta vehículos en parques naturales?", a: "Sí, incluye modelos específicos para motores y rodadura de neumáticos en zonas protegidas." },
        { q: "¿Se pueden entrenar ruidos industriales a medida?", a: "Sí, JALUD Embedded entrena modelos neuronales para cualquier sonido industrial específico." }
      ]
    }
  },
  ar: {
    gunshot: {
      eyebrow: "الكشف الفوري عن إطلاق النار",
      title: "الكشف عن إطلاق النار في الوقت الفعلي",
      lead: "كشف فوري لإطلاق النار في أقل من 400 milisecond وتوجيه كاميرات PTZ تلقائياً.",
      gain: "استجابة طوارئ أسرع بـ 7 مرات",
      filter: "مرشح النبضات الصوتية",
      steps: [{ icon: "💥", title: "1. الموجة الصدمية", desc: "يلتقط موجة انفجار السلاح." }],
      intro: "تحديد موقع إطلاق النار صوتياً وتوجيه الكاميرات خلال 0.4 ثانية.",
      sections: [{ title: "تحليل موجات الانفجار", desc: "تصفية الألعاب النارية والضوضاء.", bullets: [] }],
      faqs: [{ q: "ما هي سرعة الكشف؟", a: "الكشف في أقل من 0.4 ثانية." }]
    },
    glass: {
      eyebrow: "كشف كسر الزجاج",
      title: "الكشف عن كسر الزجاج في الوقت الفعلي",
      lead: "تعرف فوري على كسر زجاج النوافذ لمنع السرقة.",
      gain: "إنذار محيطي فوري",
      filter: "مرشح رنين الزجاج",
      steps: [{ icon: "🔨", title: "1. الصدمة", desc: "يلتقط ضربة الزجاج الأولى." }],
      intro: "كشف كسر الزجاج قبل دخول المتسللين.",
      sections: [{ title: "تحليل التردد المزدوج", desc: "دمج الصدمة ورنين الزجاج.", bullets: [] }],
      faqs: [{ q: "هل يحدث إنذارات كاذبة؟", a: "لا، التحليل المزدوج يمنع الإنذارات الخاطئة." }]
    },
    drone: {
      eyebrow: "حماية المجال الجوي",
      title: "الكشف الصوتي عن الطائرات المسيرة (الدرون)",
      lead: "يستخدم نظام SED الذكاء الاصطناعي للتعرف على الصوت المميز لمحركات ومراوح الطائرات المسيرة، مما يوفر إنذاراً مبكراً بوجودها قرب البنية التحتية الحيوية والمرافق المحمية.",
      demoBtn: "طلب عرض توضيحي مباشر",
      gain: "إنذار مبكر للمجال الجوي",
      filter: "مرشح تردد المحركات",
      steps: [{ icon: "🛸", title: "1. كشف المحركات", desc: "يلتقط صوت محركات الدرون." }],
      intro: "حماية الحرم الجوي من الدرونز غير المصرح بها.",
      sections: [{ title: "التردد التوافقي", desc: "التعرف على دوارات الدرون.", bullets: [] }],
      faqs: [{ q: "ما هي مسافة الكشف؟", a: "يكتشف الدرون على بعد مئات الأمتار." }]
    },
    scream: {
      eyebrow: "ذكاء اصطناعي صوتي متعدد الفئات وتهدئة النزاعات",
      title: "تصنيف الصراخ والذعر والعدوان اللفظي",
      lead: "تصنف الشبكات العصبية لـ SED بدقة 5 فئات صوتية: صراخ النساء، صراخ الرجال، صراخ الأطفال، بكاء الرضع، والعدوان اللفظي، مما يتيح التدخل الوقائي قبل وقوع الاعتداء.",
      gain: "تهدئة النزاعات وتصنيف أولويات الطوارئ",
      filter: "شبكة عصبية خماسية للإجهاد الصوتي والرنين",
      steps: [
        { icon: "⚡", title: "1. الصراخ مقابل العدوان", desc: "يميز بين صراخ الذعر (حادث قيد الحدوث) والشجار اللفظي العدواني (إنذار مبكر قبل العنف)." },
        { icon: "👶", title: "2. تصنيف العمر والجنس", desc: "يعزل بصمات صوتية لصراخ النساء وصراخ الرجال وصراخ الأطفال وبكاء الرضع." },
        { icon: "🔒", title: "3. حماية الخصوصية 100% (GDPR/HIPAA)", desc: "تحليل طيفي موضعي بدون تسجيل صوتي أو تنصت على المحادثات." }
      ],
      intro: "على الرغم من أن صراخ الذعر والشجار العدواني يمثلان أصواتاً بشرية مرتفعة، إلا أن بصماتهما الصوتية وإجراءات التعامل معهما مختلفة تماماً. يتضمن نظام SED مصنفات عصبية منفصلة لصراخ النساء وصراخ الرجال وصراخ الأطفال وبكاء الرضع والعدوان اللفظي، مما يتيح للطواقم الأمنية والطبية تهدئة النزاعات قبل حدوث اعتداء جسدي.",
            sections: [
        {
          title: "خمسة مصنفات مستقلة للأحداث الصوتية البشرية",
          desc: "تميز الشبكات العصبية خمسة أنواع من الأحداث الصوتية لتوفير إنذار مبكر يتطلب التحقق والاستجابة الفورية:",
          bullets: [
            "**صرخة ذعر نسائية:** التعرف على الانفعالات الصوتية الناتجة عن الخوف الشديد أو التهديد المباشر.",
            "**صرخة ذعر رجالية:** كشف التعبيرات الصوتية العاجلة التي تشير إلى مواقف خطيرة أو إصابات.",
            "**صرخة ذعر أطفال:** التعرف على صرخات الأطفال الحادة للتحذير من المخاطر أو السقوط.",
            "**بكاء الأطفال:** كشف بكاء الرضع والأطفال لإخطار المشغلين فوراً والمساعدة في العثور عليهم.",
            "**الاعتداء اللفظي والشجار:** كشف الصراخ والتهديدات الصوتية والمشاجرات المتصاعدة."
          ]
        },
        {
          title: "أهمية التمييز بين الصراخ والاعتداء اللفظي في الميدان",
          desc: "يساعد التمييز بين الأنواع الصوتية المختلفة المشغلين على تقييم الموقف بدقة واختيار الاستجابة المناسبة:",
          bullets: [
            "**استجابة أسرع:** يتيح التنبيه المبكر التدخل لتهدئة النزاع قبل وقوع عنف جسدي.",
            "**تقييم دقيق للحدث:** يتلقى المشغل تصنيفاً صوتياً دقيقاً مرتبطاً بتوجيه كاميرات PTZ الذكية.",
            "**تقليل الإنذارات الكاذبة:** يقوم الذكاء الاصطناعي بفلترة الأصوات والضوضاء البيئية بدقة عالية."
          ]
        }
      ],
      faqs: [
        { q: "هل يستطيع SED التمييز بين صراخ الذعر والشجار العدواني؟", a: "نعم. يحتوي النظام على نماذج عصبية مستقلة لكل من الصراخ (خطر داهم) والعدوان اللفظي (شجار وصراخ عدائي) للتدخل وتهدئة النزاع قبل حدوث العنف الجسدي." },
        { q: "كيف يميز النظام بين صراخ النساء والرجال والأطفال؟", a: "تحلل الشبكة العصبية النغمة الصوتية الأساسية وتغيرات الرنين وأنماط إجهاد الصوت." }
      ]
    },
    graffiti: {
      eyebrow: "حماية المباني من التخريب",
      title: "كشف الرش والرسم في الوقت الفعلي",
      lead: "كشف صفير عبوات الطلاء ورج الكرة المعدنية قبل تخريب الجدران.",
      gain: "حماية المباني",
      filter: "مرشح رذاذ Aerosol",
      steps: [{ icon: "🎨", title: "1. الصفير والكرة", desc: "يلتقط صوت عبوة الرش." }],
      intro: "حماية المباني والآثار من التخريب بالطلاء.",
      sections: [{ title: "صفير الرش", desc: "التعرف على صوت فوهة الرش.", bullets: [] }],
      faqs: [{ q: "ما هي الأصوات المحفزة للإنذار؟", a: "صوت صفير الرش مع رج الكرة المعدنية." }]
    },
    grinder: {
      eyebrow: "حماية المحيط الصناعي",
      title: "كشف أدوات القطع والحفر",
      lead: "كشف أدوات قطع المعادن والمناشر في المحطات والخزائن.",
      gain: "حماية السياج المحيطي",
      filter: "مرشح احتكاك القطع",
      steps: [{ icon: "⚙️", title: "1. احتكاك المحرك", desc: "يلتقط قطع المعادن." }],
      intro: "حماية المحيط من أدوات القطع الثقيلة.",
      sections: [{ title: "نمط القطع", desc: "تصفية صوت الأدوات عن الحركة المرورية.", bullets: [] }],
      faqs: [{ q: "ما هي الأدوات المكتشفة؟", a: "المناشر الكهربائية، المثاقب، ومجالي المعادن." }]
    },
    siren: {
      eyebrow: "المرور الذكي وإنذار السيارات",
      title: "كشف صفارات الإنذار وإنذار السيارات",
      lead: "كشف صفارات الطوارئ لإعطاء الأولوية في الإشارات الذكية.",
      gain: "الأولوية لسيارات الإسعاف",
      filter: "مرشح صفارات الطوارئ",
      steps: [{ icon: "🚨", title: "1. تتبع الصفارة", desc: "يلتقط سيارات الطوارئ." }],
      intro: "إعطاء الموجة الخضراء لسيارات الإسعاف والشرطة.",
      sections: [{ title: "تتبع الصفارات", desc: "تسريع مرور الطوارئ في التقاطعات.", bullets: [] }],
      faqs: [{ q: "كيف يساعد الحركة المرورية؟", a: "يمنح الأولوية الخضراء للإسعاف." }]
    },
    other: {
      eyebrow: "مصنفات صوتية موسعة وذكاء اصطناعي مخصص",
      title: "أصوات أخرى / كشف مخصص",
      lead: "يتعرف نظام SED على كتالوج شامل لتنبيهات الأمان (Alerts) والأحداث البيئية (Events)، بالإضافة إلى تدريب نماذج الذكاء الاصطناعي المخصصة للعملاء.",
      gain: "ذكاء اصطناعي صوتي شامل",
      filter: "مصنف طرفي متعدد الفئات وأبحاث مخصصة",
      steps: [
        { icon: "🚨", title: "1. تنبيهات الأمان الحرجة", desc: "كشف دخول المركبات في المناطق النائية، صرير الإطارات، الألعاب النارية، اختبار الزجاج، وصراخ الذعر للرجال والأطفال." },
        { icon: "📊", title: "2. الأحداث البيئية والتشغيلية", desc: "كشف إغلاق الأبواب في الكراجات، أدوات البناء، التواجد البشري في الغرف المعقمة، بكاء الرضع، وحركة النقل." },
        { icon: "🎛️", title: "3. نماذج عصبية مخصصة", desc: "تدريب نماذج ذكاء اصطناعي على أصوات العميل (تسرب الغاز الصناعي، أعطال محامل الآلات)." }
      ],
      intro: "تستطيع أجهزة كشف الصوت SED تحديد مجموعة واسعة من الحوادث الصوتية، مصنفة إلى تنبيهات (Alerts) وأحداث (Events). تشير التنبيهات إلى مواقف خطيرة أو غير مرغوب فيها، بينما توفر الأحداث معلومات بيئية قيمة مثل كشف التواجد البشري في المناطق المحظورة.",
            sections: [
        {
          title: "اكتشافات أمنية موسعة",
          desc: "بالإضافة إلى كشف إطلاق النار والزجاج والصراخ والدرون والجرافيتي، يكشف SED أحداثاً أمنية إضافية:",
          bullets: [
            "**المركبات:** كشف أصوات محركات المركبات للتحذير من الدخول غير المصرح به للمناطق المحمية.",
            "**حركة المرور:** مراقبة صوت احتكاك إطارات السيارات بالطرق في المحيطات البعيدة.",
            "**صرير الإطارات:** كشف الانجراف والفرملة المفاجئة التي تشير إلى مخاطر مرورية.",
            "**الألعاب النارية:** كشف المقذوفات النارية لحماية المعالم والمنشآت.",
            "**جهاز فحص الزجاج:** التعرف على أجهزة المعايرة لتسهيل التركيب."
          ]
        },
        {
          title: "الكشف عن الأحداث التشغيلية والبيئية",
          desc: "يوسع الإصدار الكامل إمكانيات النظام بالأصوات البيئية للمدن والمستودعات والمرافق الصناعية:",
          bullets: [
            "**إغلاق الأبواب بقوة:** كشف صفق الأبواب في المناطق الحساسة أو خارج أوقات العمل.",
            "**أعمال البناء:** التعرف على أصوات المثاقب والمناشير ومعدات الهدم.",
            "**التواجد البشري:** كشف أصوات الكلام والضحك والسعال في المناطق المعقمة والمغلقة.",
            "**الأطفال:** التعرف على أصوات لعب أو صراخ الأطفال في المناطق المراقبة.",
            "**الحيوانات والطيور:** تتبع أصوات الطيور والحيوانات لسلامة محيط المطارات.",
            "**النقل والقطارات:** التعرف على بصمات أصوات الترام والقطارات والحافلات.",
            "**نغمات التحذير والأجراس:** كشف أبواق المركبات وأجراس الإنذار.",
            "**الموسيقى والغناء:** التعرف على الآلات الموسيقية في المناطق الهادئة.",
            "**الصرير الميكانيكي:** كشف الاحتكاك الميكانيكي للبوابات والأبواب."
          ]
        }
      ],
      faqs: [
        { q: "ما هو الفرق بين التنبيهات والأحداث؟", a: "التنبيهات تدل على أخطار حرجة تتطلب تدخلاً فورياً، بينما توفر الأحداث وعياً بيئياً مستمراً." },
        { q: "هل يمكن كشف المركبات في المحميات الطبيعية؟", a: "نعم، يحتوي النظام على نماذج مخصصة لأصوات المحركات واحتكاك الإطارات في المناطق النائية." },
        { q: "هل يمكن تدريب النظام على أصوات مصانع خاصة؟", a: "نعم، نقوم بتسجيل وتدريب نماذج عصبية لأي أصوات صناعية مخصصة." }
      ]
    }
  },
  pt: {
    gunshot: {
      eyebrow: "DETEÇÃO DE DISPAROS EM TEMPO REAL",
      title: "Deteção de Disparos em Tempo Real",
      lead: "Deteção instantânea de tiros em menos de 400ms com rotação automática de câmaras PTZ.",
      gain: "Despacho 7x mais rápido",
      filter: "Classificador de Impulsos",
      steps: [{ icon: "💥", title: "1. Onda de Choque", desc: "Detecta a onda de choque do tiro." }],
      intro: "Localização acústica de disparos em tempo real direcionando câmaras PTZ em <5s.",
      sections: [{ title: "Análise da Onda de Choque", desc: "Filtra fogos de artifício e ruídos de tráfego.", bullets: [] }],
      faqs: [{ q: "Qual a velocidade da deteção?", a: "Resposta em <5s." }]
    },
    glass: {
      eyebrow: "QUEBRA DE VIDRO E INVASÃO",
      title: "Deteção de Quebra de Vidro",
      lead: "Identificação acústica de quebra de janelas para prevenir assaltos.",
      gain: "Alerta Perimetral Instantâneo",
      filter: "Filtro de Ressonância",
      steps: [{ icon: "🔨", title: "1. Impacto", desc: "Capta o impacto inicial no vidro." }],
      intro: "Deteção de vidros partidos antes dos invasores entrarem no edifício.",
      sections: [{ title: "Análise de Dupla Frequência", desc: "Combina impacto e ressonância.", bullets: [] }],
      faqs: [{ q: "Gera falsos alarmes?", a: "Não, a análise de dupla frequência previne falsos alarmes." }]
    },
    drone: {
      eyebrow: "PROTEÇÃO DO ESPAÇO AÉREO",
      title: "Deteção Acústica de Drones",
      lead: "O SED utiliza inteligência artificial para reconhecer o som característico dos rotores e motores de drones, emitindo alertas precoces sobre a sua presença perto de infraestruturas críticas.",
      demoBtn: "Solicitar Demonstração ao Vivo",
      gain: "Alerta Antecipado do Espaço Aéreo",
      filter: "Filtro Harmónico de Rotores",
      steps: [{ icon: "🛸", title: "1. Captura de rotores", desc: "Capta frequências de motores de drones." }],
      intro: "Deteção de drones não autorizados em zonas sensíveis.",
      sections: [{ title: "Frequência Harmónica", desc: "Identifica rotações das hélices.", bullets: [] }],
      faqs: [{ q: "A que distância detecta drones?", a: "A centenas de metros de distância." }]
    },
    scream: {
      eyebrow: "IA VOCAL MULTICLASSE E DESESCALADA PREVENTIVA",
      title: "Deteção de Gritos, Pânico e Agressão Verbal",
      lead: "As redes neuronais do SED classificam 5 categorias acústicas vocais: grito feminino, grito masculino, grito infantil, choro de bebés e agressão verbal, possibilitando a desescalada preventiva.",
      gain: "Desescalada Preventiva e Triagem de Incidentes",
      filter: "Rede Neuronal de Formantes e Stress Vocal de 5 Classes",
      steps: [
        { icon: "⚡", title: "1. Gritos vs. Agressão Verbal", desc: "Diferencia gritos agudos de pânico (incidente em curso) de discussões e agressões verbais hostis (fase pré-violência)." },
        { icon: "👶", title: "2. Classificação por Idade e Género", desc: "Isola formantes acústicos para gritos de mulheres, homens, crianças e choro de bebés." },
        { icon: "🔒", title: "3. 100% Conforme GDPR e HIPAA", desc: "Análise espectral em chips Edge sem gravação de voz ou escuta de conversas privadas." }
      ],
      intro: "Embora os gritos de pânico e a agressão verbal envolvam vocalizações humanas intensas, os seus padrões acústicos e ações de resposta são completamente distintos. O Sound Event Detector dispõe de classificadores neuronais dedicados para Grito Feminino, Grito Masculino, Grito Infantil, Choro de Bebé e Agressão Verbal. Esta distinção permite às equipas hospitalares e policiais desescalar discussões verbais antes que ocorra violência física.",
            sections: [
        {
          title: "Cinco classificadores independentes de eventos de voz",
          desc: "As redes neuronais distinguem cinco tipos de eventos vocais para rápida verificação e resposta:",
          bullets: [
            "**Grito de pânico feminino:** Reconhecimento de vocalizações associadas a perigo iminente ou medo.",
            "**Grito de pânico masculino:** Deteção de expressões vocais de emergência perante agressões ou acidentes.",
            "**Grito de pânico infantil:** Reconhecimento de gritos agudos de crianças para aviso de incidentes.",
            "**Choro de bebé:** Deteção de choro infantil para notificação rápida e localização em recintos.",
            "**Agressão verbal e discussões:** Reconhecimento de ameaças, gritos hostis e conflitos em escalada."
          ]
        },
        {
          title: "Por que distinguir grito de agressão é crucial na prática",
          desc: "A capacidade de diferenciar eventos vocais ajuda os operadores a avaliar incidentes com precisão:",
          bullets: [
            "**Resposta mais rápida:** O alerta precoce permite intervir antes que a violência física ocorra.",
            "**Avaliação rigorosa do evento:** O operador recebe a classificação sonora associada às câmaras PTZ.",
            "**Redução de falsos alarmes:** A IA filtra ruídos urbanos quotidianos com elevada precisão."
          ]
        }
      ],
      faqs: [
        { q: "O SED consegue distinguir entre um grito de pânico e uma discussão agressiva?", a: "Sim. O SED utiliza modelos neuronais separados para Gritos (pânico imediato/ataque) e Agressão (discussões verbais e hostilidade), permitindo intervir antes da violência física." },
        { q: "Como distingue entre gritos de mulheres, homens e crianças?", a: "A rede neuronal analisa o tom fundamental (F0), os formantes acústicos e os padrões de stress harmónico da voz." }
      ]
    },
    graffiti: {
      eyebrow: "PROTEÇÃO CONTRA VANDALISMO",
      title: "Deteção de Graffiti e Spray",
      lead: "Detecta o silvo da lata de spray antes de danificar superfícies.",
      gain: "Superfícies sem Vandalismo",
      filter: "Filtro Acústico de Aerossol",
      steps: [{ icon: "🎨", title: "1. Silvo e esfera", desc: "Detecta o spray e a esfera metálica." }],
      intro: "Evita pichações por meio de alerta antecipado.",
      sections: [{ title: "Silvo do Aerossol", desc: "Identifica o bocal do spray.", bullets: [] }],
      faqs: [{ q: "Que sons ativam o alarme?", a: "Silvo de pressão e a esfera misturadora metálica." }]
    },
    grinder: {
      eyebrow: "PROTEÇÃO INDUSTRIAL PERIMETRAL",
      title: "Deteção de Esmerilhadeiras e Perfuração",
      lead: "Detecta rebarbadoras e ferramentas de corte em vedações e cofres.",
      gain: "Proteção Perimetral",
      filter: "Classificador de Fricção",
      steps: [{ icon: "⚙️", title: "1. Fricção", desc: "Capta corte de metal." }],
      intro: "Proteção contra corte de vedações e ferramentas pesadas.",
      sections: [{ title: "Padrão de Corte", desc: "Separa rebarbadoras do tráfego habitual.", bullets: [] }],
      faqs: [{ q: "Que ferramentas são detectadas?", a: "Esmerilhadeiras, brocas e serras de metal." }]
    },
    siren: {
      eyebrow: "TRÂNSITO INTELIGENTE E ALARMES",
      title: "Deteção de Sirenes e Alarmes",
      lead: "Detecta sirenes de emergência para priorização de semáforos.",
      gain: "Prioridade de Sinal Verde",
      filter: "Filtro de Sirenes",
      steps: [{ icon: "🚨", title: "1. Rastreio de sirene", desc: "Capta veículos de emergência." }],
      intro: "Prioridade de semáforos para ambulâncias e polícia.",
      sections: [{ title: "Rastreio de Sirenes", desc: "Acelera a passagem de emergências.", bullets: [] }],
      faqs: [{ q: "Como ajuda o trânsito?", a: "Conecta a onda verde para veículos de emergência." }]
    },
    other: {
      eyebrow: "CLASSIFICADORES ACÚSTICOS ALARGADOS E IA PERSONALIZADA",
      title: "Outros / Áudio personalizado",
      lead: "O SED identifica um catálogo abrangente de Alertas de segurança e Eventos ambientais, além de modelos neuronais de IA treinados sob medida para aplicações de clientes.",
      gain: "IA Acústica de Espectro Total",
      filter: "Classificador Edge Multi-Classe e I&D",
      steps: [
        { icon: "🚨", title: "1. Alertas Críticos de Segurança", desc: "Reconhece veículos em áreas remotas, guincho de pneus/drifting, fogo de artifício, tester de vidro, gritos de pânico de homens e crianças." },
        { icon: "📊", title: "2. Eventos Ambientais e Operacionais", desc: "Deteta bater de portas em garagens, ferramentas/obras, presença humana em salas estéreis, choro de bebés e transportes." },
        { icon: "🎛️", title: "3. Modelos de IA Sob Medida", desc: "Treina modelos leves diretamente nos chips e processadores edge (Axis, Hanwha, Hikvision...) com amostras do cliente (fugas de gás, falhas de rolamentos)." }
      ],
      intro: "Os detetores sonoros SED são capazes de identificar uma ampla gama de incidentes acústicos, divididos em Alertas (Alerts) e Eventos (Events). Os alertas indicam situações perigosas ou indesejadas, enquanto os eventos correspondem a sons ambientais que fornecem inteligência situacional vital (por exemplo, detetar presença humana ou portas a bater em áreas restritas).",
            sections: [
        {
          title: "Deteções de Segurança Alargadas",
          desc: "Além das deteções dedicadas de tiros, quebra de vidros, gritos, drones e graffiti, o SED reconhece alertas adicionais:",
          bullets: [
            "**Veículo:** Reconhecimento de motor de veículos em passagens e perímetros protegidos.",
            "**Tráfego rodoviário:** Monitorização acústica da fricção de pneus em vias perimetrais.",
            "**Chiar de pneus:** Reconhecimento de derrapagens ou travagens bruscas em vias públicas.",
            "**Fogo de artifício:** Deteção de pirotecnia para proteção de recintos e património.",
            "**Testador de vidro:** Reconhecimento de calibradores para fácil instalação."
          ]
        },
        {
          title: "Deteções Situacionais e Operacionais",
          desc: "A versão completa acrescenta sons operacionais para cidades, armazéns, prisões e indústria:",
          bullets: [
            "**Bater de portas:** Deteção de portas batidas com força em áreas técnicas ou restritas.",
            "**Trabalhos de construção:** Reconhecimento de berbequins, serras e martelos pneumáticos.",
            "**Presença humana:** Reconhecimento de voz, riso, tosse ou sussurros em salas protegidas.",
            "**Crianças:** Reconhecimento de crianças a brincar ou a gritar em recintos.",
            "**Animais e aves:** Rastreio acústico de fauna para segurança perimetral.",
            "**Transportes e comboios:** Assinaturas sonoras de elétricos, comboios e autocarros.",
            "**Sinais sonoros de aviso:** Deteção de buzinas de tráfego e sinos de emergência.",
            "**Música e canto:** Reconhecimento de instrumentos musicais em zonas de repouso.",
            "**Ruídos mecânicos:** Deteção de fricção e chiar em portões e portas metálicas."
          ]
        }
      ],
      faqs: [
        { q: "Qual a diferença entre Alertas e Eventos?", a: "Alertas indicam perigos urgentes (tiros, gritos, derrapagens). Eventos fornecem consciência ambiental contínua (presença humana, bater de portas)." },
        { q: "Deteta veículos em parques nacionais?", a: "Sim, inclui modelos para motores e rodagem de pneus desenhados para áreas protegidas." },
        { q: "Podem ser treinados ruídos industriais sob medida?", a: "Sim, a JALUD Embedded treina modelos de IA para qualquer som industrial específico." }
      ]
    }
  }
};
